import React, { useRef, useContext, useState, useEffect, useCallback } from 'react';
import { COLORS } from '@app/src/ui';
import { isClientEnv } from '@app/src/utils/validations';
import { Icon } from '@app/src/common/Icon';
import {
  Wrapper,
  Content,
  IconWrapper,
  InputWrapper,
  Placeholder,
  IconClose,
  LoaderWrapper,
  Ubigeo
} from './styled';
import { SearchJobContext } from './SearchConstructor';
import { SearchResults } from './SearchResults';

const isClient = isClientEnv();
const KEY_DOWN = 40;
const KEY_UP = 38;
const ESC = 27;
const ENTER = 13;

export const SearchInput: React.FC = () => {
  const {
    error,
    input,
    disabled,
    handleChange,
    handleChangeInput,
    handleBlurInput,
    handleClickClose,
    defaultJob,
    dataList,
    isLoading,
    message,
    placeholder,
    minCharForSearch,
    lastWordFound
  } = useContext(SearchJobContext);
  const [currentValue, setCurrentValue] = useState('');
  const [value, setValue] = useState('');
  const [suggestionList, setSuggestionList] = useState([]);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  let refItems = null;
  const refInput = useRef(null);
  const refWrapper = useRef(null);
  const refClose = useRef(null);

  useEffect(() => {
    if (currentValue.length >= minCharForSearch && currentValue !== value && !isLoading) {
        setSuggestionList(dataList);
        refInput.current.focus();
    }
    if (currentValue.length < minCharForSearch) {
      setSuggestionList([]);
    }
  }, [dataList, currentValue, value, isLoading]);

  useEffect(() => {
    if (defaultJob) {
      setValue(defaultJob);
      setCurrentValue(defaultJob);
      refInput.current.style.display ='none';
    } else {
      setValue('');
      setCurrentValue('');
      refInput.current.style.display ='block';
    }
  }, [defaultJob]);

  useEffect(() => {
    if(isClient) {
      if(suggestionList && suggestionList.length > 0) {
        document.addEventListener('click', handleCloseSuggestion);
      }
      return () => {
        document.removeEventListener('click', handleCloseSuggestion);
      };
    }
  }, [suggestionList]);

  useEffect(() => {
    if(suggestionList[activeSuggestion]) {
      setCurrentValue(suggestionList[activeSuggestion].name);
      setValue(suggestionList[activeSuggestion].name);
      refItems[activeSuggestion].current.focus();
      refInput.current.style.display ='none';
    }
    if(activeSuggestion < 0) {
      setSuggestionList([]);
    }

  }, [activeSuggestion]);

  const handleCloseSuggestion = (e: MouseEvent) => {
    if(!refWrapper.current.contains(e.target)) {
      setSuggestionList([]);
    }
  };

  useEffect(() => {
    if (currentValue !== lastWordFound && lastWordFound !== '') {
      handleChangeInput && handleChangeInput(currentValue);
    }
  }, [lastWordFound]);

  const handleChangeProp = useCallback((e) => {
    const current = e.target.value.replace(/\s\s+|\n/g, ' ');
    setCurrentValue(current);
    if (current.length >= minCharForSearch && current !== value && !isLoading) {
      handleChangeInput && handleChangeInput(current);
    }
  }, [value, isLoading]);

  const handleClickSuggestion = (indexActive: number) => {
    setActiveSuggestion(indexActive);
    refInput.current.style.display ='none';
    setTimeout(() => {
      setSuggestionList([]);
    }, 200);
    handleChange && handleChange(suggestionList[indexActive]);
  };

  const handleClickClear = () => {
    refInput.current.style.display = 'block';
    setSuggestionList([])
    setCurrentValue('');
    setValue('');
    handleClickClose && handleClickClose();
  };

  const handleKeyDownList = (e: React.KeyboardEvent) => {
    e.preventDefault();
    const keyCode = e.keyCode || e.which;
    if(keyCode === KEY_DOWN) {
      if(activeSuggestion < suggestionList.length - 1) {
        setActiveSuggestion(activeSuggestion + 1);
      }
    }
    if(keyCode === KEY_UP) {
      setActiveSuggestion(activeSuggestion - 1);
    }
    if(keyCode === ENTER) {
      setActiveSuggestion(activeSuggestion);
      handleChange && handleChange(suggestionList[activeSuggestion]);
      setActiveSuggestion(-1);
    }
    if(keyCode === ESC) {
      setSuggestionList([]);
    }
  };

  const handleBlur = () => {
    if(activeSuggestion === -1) {
      setSuggestionList([]);
      setValue('');
      handleChange && handleChange(null);
      handleBlurInput && handleBlurInput();
    }
  };

  const handleKeyPress = (e) => {
    if (e.keyCode === ENTER) {
      e.preventDefault();
    }
  };

  const handleKeyDownInput = (e: React.KeyboardEvent) => {
    const keyCode = e.keyCode || e.which;
    if(keyCode === KEY_DOWN) {
      if(suggestionList.length) {
        setActiveSuggestion(0);
      }
    }
    if(keyCode === ENTER) {
      e.preventDefault();
      setSuggestionList([]);
    }
  };

  return(
  <Content>
    <Wrapper
      ref={refWrapper}
      borderColor={`1px solid ${ error ? 'red' : COLORS.GRAY}`}
    >
      <IconWrapper>
        <Icon data-name="icon-map" width="18" height="18" fill={COLORS.GRAY_PLACEHOLDER} />
      </IconWrapper>
      <InputWrapper
        {...input}
        ref={refInput}
        disabled={disabled}
        onBlur={handleBlur}
        onKeyPress={handleKeyPress}
        onChange={handleChangeProp}
        value={currentValue}
        onKeyDown={handleKeyDownInput}
        autoComplete="off"
      />
      {
        (currentValue === '' && value === '') &&
          <Placeholder>
            <span>{placeholder}</span>
          </Placeholder>
      }
      {
        (currentValue && currentValue === value) &&
          <Ubigeo>
            <span>{value}</span>
          </Ubigeo>
      }
      {
        (currentValue !== '' || value !== '') && !disabled && !isLoading &&
          <IconClose onClick={handleClickClear} ref={refClose}>
            <Icon data-name="icon-close-bold" width="18" height="18" fill={COLORS.GRAY} />
          </IconClose>
      }
      {
        isLoading &&
        <LoaderWrapper>
            <Icon data-name='icon-circle' fill='#ccc' width={'17'} height={'17'} />
        </LoaderWrapper>
      }
      {
        <SearchResults
          dataList={suggestionList}
          showFirstItems={5}
          handleClickItem={handleClickSuggestion}
          handleRefItems={refs => { refItems = refs }}
          handleKeyDown={handleKeyDownList}
          message={message}
        />
      }
    </Wrapper>
  </Content>
)};
